import { THRESHOLDS, RESULT } from './thresholds.js';

export function evaluateMetric(value, threshold) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return RESULT.NO_DATA;
  }
  if (threshold.direction === 'higher-is-better') {
    if (value >= threshold.pass) return RESULT.PASS;
    if (value >= threshold.borderline) return RESULT.BORDERLINE;
    return RESULT.FAIL;
  }
  if (value <= threshold.pass) return RESULT.PASS;
  if (value <= threshold.borderline) return RESULT.BORDERLINE;
  return RESULT.FAIL;
}

// MOS computed from a simplified E-model R-factor for VoIP pre-screening.
// Loosely follows the structure of ITU-T G.107 but uses an aggressive
// log-shaped loss-impairment term widely used in third-party VoIP QoE
// approximations rather than G.107's strict Ie_eff curve. Calibration of
// the formula's behavior against real Associate measurements happens in
// Phase 3; the current numbers are a conservative starting point.
//
// Inputs: packetLossPct (0..100), jitterMs, rttMs.
// Output: MOS in [1.0, 4.5], rounded to 2 decimals.
export function computeMos({ packetLossPct, jitterMs, rttMs }) {
  // If any required input is missing or invalid, refuse to fabricate a number.
  // The caller (evaluateAll) treats null as "metric unavailable" -> FAIL verdict.
  const isInvalid = v => v === null || v === undefined || Number.isNaN(Number(v));
  if (isInvalid(packetLossPct) || isInvalid(jitterMs) || isInvalid(rttMs)) {
    return null;
  }
  const loss = Math.max(0, Math.min(100, Number(packetLossPct)));
  const jitter = Math.max(0, Number(jitterMs));
  const rtt = Math.max(0, Number(rttMs));

  // One-way delay ~ rtt/2 + jitter buffer (2x jitter conservatively).
  const oneWayDelay = rtt / 2 + jitter * 2;

  // Delay impairment Id (per ITU G.107 simplified form).
  // 0..177.3ms: linear-ish; >177.3ms: piecewise.
  let Id;
  if (oneWayDelay < 177.3) {
    Id = 0.024 * oneWayDelay;
  } else {
    Id = 0.024 * oneWayDelay + 0.11 * (oneWayDelay - 177.3);
  }

  // Equipment impairment Ie + packet loss impairment.
  // For G.711 baseline: Ie ~ 0; loss impact via Burst/Random model.
  // Simplified: Ie_effective = 30 * ln(1 + 15 * lossFraction)
  const lossFraction = loss / 100;
  const Ie_eff = 30 * Math.log1p(15 * lossFraction);

  // R-factor.
  // R0 (signal-to-noise baseline) = 93.2; Is (simultaneous impairments) negligible for VoIP screening; A (advantage factor) = 0.
  let R = 93.2 - Id - Ie_eff;
  if (R < 0) R = 0;
  if (R > 100) R = 100;

  // R -> MOS conversion. R is already clamped to [0, 100], so the standard
  // polynomial below is sufficient. The Math.max/Math.min line below is a
  // safety net for floating-point edge cases.
  let mos = 1 + 0.035 * R + 7e-6 * R * (R - 60) * (100 - R);
  mos = Math.max(1.0, Math.min(4.5, mos));
  return Math.round(mos * 100) / 100;
}

export function evaluateAll(metrics) {
  const augmented = {
    ...metrics,
    mosScore: metrics.mosScore !== undefined
      ? metrics.mosScore
      : computeMos({
          packetLossPct: metrics.packetLossPct,
          jitterMs: metrics.jitterMs,
          rttMs: metrics.rttMs
        })
  };

  const verdicts = {};
  for (const [key, threshold] of Object.entries(THRESHOLDS)) {
    verdicts[key] = evaluateMetric(augmented[key], threshold);
  }
  if (augmented.downloadTimedOut === true) verdicts.downloadMbps = RESULT.NO_DATA;
  if (augmented.uploadTimedOut === true) verdicts.uploadMbps = RESULT.NO_DATA;
  return { metrics: augmented, verdicts };
}

export function overallResult(verdicts) {
  const values = Object.values(verdicts);
  if (values.includes(RESULT.INCONCLUSIVE)) return RESULT.INCONCLUSIVE;
  if (values.includes(RESULT.NO_DATA)) return RESULT.INCONCLUSIVE;
  if (values.includes(RESULT.FAIL)) return RESULT.FAIL;
  if (values.includes(RESULT.BORDERLINE)) return RESULT.BORDERLINE;
  return RESULT.PASS;
}
